import { useCallback, useState } from 'react';
import { useLocalSessionId, useParticipantIds, useScreenShare } from '@daily-co/daily-react';
import { PipExitIcon } from '@icons/PipExitIcon';
import { PipIcon } from '@icons/PipIcon';
import {
  ArrowBack as ArrowBackIcon,
  FiberManualRecord as RecordIcon,
  Logout as LogoutIcon,
  Mic as MicIcon,
  MicOff as MicOffIcon,
  ScreenShare as ScreenShareIcon,
  StopScreenShare as StopScreenShareIcon,
  Videocam as VideocamIcon,
  VideocamOff as VideocamOffIcon,
} from '@mui/icons-material';
import { Box, ButtonBase, Stack, Tooltip, Typography } from '@mui/material';
import type { AudioInputDeviceService } from '@services/audio';
import type { DailyCallService } from '@services/dailycall';
import { useDailyCallState } from '@services/dailycall';
import { useEventTracking } from '@services/statsig';
import { useAppSelector } from '@store/hooks';

import { useCallLayout } from '../../hooks/useCallLayout';
import { selectHideSelfView } from '../../store';
import type { ManualLayoutChoice, TelehealthSession } from '../../types';
import { CameraDropdown } from '../CameraDropdown';
import { GridView } from '../GridView';
import { LayoutToggle } from '../LayoutToggle';
import { MicDropdown } from '../MicDropdown';
import { SpeakerView } from '../SpeakerView';
import { SplitMediaButton } from '../SplitMediaButton';
import { VideoSpotlight } from '../VideoSpotlight';
import { WaitingParticipants } from '../WaitingParticipants';

interface PipViewProps {
  dailyCallService: DailyCallService;
  audioInputDeviceService: AudioInputDeviceService | null;
  setSelectedMicrophone: (mic: MediaDeviceInfo) => void;
  pipWindow?: Window;
  isRecording: boolean;
  isRecordingPaused?: boolean;
  session: TelehealthSession;
  onCopyLink?: () => void;
  isCopyingLink?: boolean;
  linkCopied?: boolean;
  onLeave: () => void;
  onClose: () => void;
  isPipWindow?: boolean;
  onEnterPip?: () => void;
  isPipSupported?: boolean;
}

export function PipView({
  dailyCallService,
  audioInputDeviceService,
  setSelectedMicrophone,
  pipWindow,
  isRecording,
  isRecordingPaused = false,
  session,
  onCopyLink,
  isCopyingLink,
  linkCopied,
  onLeave,
  onClose,
  isPipWindow = false,
  onEnterPip,
  isPipSupported = false,
}: PipViewProps) {
  const callState = useDailyCallState(dailyCallService);
  const remoteParticipantIds = useParticipantIds({ filter: 'remote' });
  const localSessionId = useLocalSessionId();
  const layout = useCallLayout();
  const { telehealth } = useEventTracking();
  const { isSharingScreen, screens, startScreenShare, stopScreenShare } = useScreenShare();
  const hideSelfView = useAppSelector(selectHideSelfView);

  const [activeDropdown, setActiveDropdown] = useState<'mic' | 'camera' | null>(null);
  const someoneElseSharing = screens.length > 0 && !isSharingScreen;

  const handleToggleMic = useCallback(() => {
    const newState = !callState.localAudioEnabled;
    dailyCallService.setLocalAudio(newState);
    telehealth.micToggled({
      appointment_id: session.appointmentId,
      enabled: newState,
    });
  }, [callState.localAudioEnabled, dailyCallService, session.appointmentId, telehealth]);

  const handleToggleVideo = useCallback(() => {
    const newState = !callState.localVideoEnabled;
    dailyCallService.setLocalVideo(newState);
    telehealth.videoToggled({
      appointment_id: session.appointmentId,
      enabled: newState,
    });
  }, [callState.localVideoEnabled, dailyCallService, session.appointmentId, telehealth]);

  const handleShareToggle = useCallback(() => {
    if (isSharingScreen) {
      stopScreenShare();
    } else {
      startScreenShare();
    }
    telehealth.screenShareToggled({
      appointment_id: session.appointmentId,
      enabled: !isSharingScreen,
    });
  }, [isSharingScreen, startScreenShare, stopScreenShare, session.appointmentId, telehealth]);

  const handleLayoutChange = useCallback(
    (choice: ManualLayoutChoice) => {
      layout.setManualLayoutChoice(choice);
      telehealth.layoutChanged({
        appointment_id: session.appointmentId,
        layout: choice,
        participant_count: callState.participantCount,
      });
    },
    [layout, session.appointmentId, callState.participantCount, telehealth],
  );

  const showStatusRow = isSharingScreen || isRecording;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        bgcolor: 'grey.700',
        overflow: 'hidden',
        gap: 1.5,
        p: 1.5,
      }}
    >
      {/* Waiting participants — full version with invite links */}
      <WaitingParticipants
        isOwner={session.isOwner}
        hasRemoteParticipants={remoteParticipantIds.length > 0}
        onCopyLink={onCopyLink}
        isCopyingLink={isCopyingLink}
        linkCopied={linkCopied}
      />

      {/* Status row — screen share + recording indicator */}
      {showStatusRow && (
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
            {isSharingScreen && (
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ bgcolor: 'grey.800', p: 1, borderRadius: 2 }}
              >
                <ScreenShareIcon sx={{ fontSize: 20, color: 'common.white' }} />
                <Typography sx={{ color: 'common.white', fontSize: 12 }}>
                  You are sharing your screen
                </Typography>
              </Stack>
            )}
          </Stack>

          {isRecording && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.5}
              sx={{ bgcolor: 'grey.800', py: 1, px: 2, borderRadius: 2 }}
            >
              <RecordIcon
                sx={{
                  fontSize: 12,
                  color: isRecordingPaused ? 'warning.main' : 'error.main',
                  animation: isRecordingPaused ? 'none' : 'pulse 1.5s ease-in-out infinite',
                  '@keyframes pulse': {
                    '0%, 100%': { opacity: 1 },
                    '50%': { opacity: 0.3 },
                  },
                }}
              />
              <Typography
                variant="caption"
                sx={{ color: isRecordingPaused ? 'warning.main' : 'error.main' }}
              >
                {isRecordingPaused ? 'Paused' : 'Recording'}
              </Typography>
            </Stack>
          )}
        </Stack>
      )}

      {/* Layout area — same IIFE pattern as CallView */}
      <Box
        sx={{
          position: 'relative',
          borderRadius: '8px',
          overflow: 'hidden',
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {(() => {
          if (layout.activeLayout === 'speaker') {
            return (
              <SpeakerView
                mainStageSessionId={layout.mainStageSessionId}
                sidebarParticipantIds={layout.sidebarParticipantIds}
                activeSpeakerId={layout.activeSpeakerId}
                localSessionId={layout.localSessionId}
                isScreenShare={layout.hasRemoteScreenShare}
                screenShareSessionId={layout.screenShareSessionId}
                fillHeight
              />
            );
          }
          if (layout.activeLayout === 'grid') {
            return (
              <GridView
                participantIds={layout.sidebarParticipantIds}
                activeSpeakerId={layout.activeSpeakerId}
                localSessionId={layout.localSessionId}
                fillHeight
              />
            );
          }
          // spotlight (default for ≤2 participants)
          return (
            <VideoSpotlight
              remoteParticipantIds={remoteParticipantIds}
              localSessionId={localSessionId ?? ''}
              fillHeight
              showLocalThumbnail={!hideSelfView}
            />
          );
        })()}
      </Box>

      {/* Controls bar — 3-column grid matching CallView */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr auto 1fr',
          alignItems: 'center',
        }}
      >
        {/* Left group: return to encounter + layout toggle */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ minWidth: 0, justifySelf: 'start' }}
        >
          {!isPipWindow && (
            <Tooltip title="Return to encounter">
              <ButtonBase
                onClick={onClose}
                aria-label="Return to encounter"
                sx={{
                  width: 48,
                  height: 48,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 1,
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                }}
              >
                <ArrowBackIcon sx={{ fontSize: 20, color: 'common.white' }} />
              </ButtonBase>
            </Tooltip>
          )}
          {layout.showLayoutToggle && (
            <LayoutToggle layout={layout.manualLayoutChoice} onChange={handleLayoutChange} />
          )}
        </Stack>

        {/* Center group: mic, camera, share, end call */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'stretch',
            gap: 1.5,
            padding: 1,
            borderRadius: 2,
            backgroundColor: 'grey.800',
          }}
        >
          {/* Mic split button */}
          <SplitMediaButton
            icon={<MicIcon sx={{ color: 'common.white' }} />}
            inactiveIcon={<MicOffIcon color="error" />}
            isActive={callState.localAudioEnabled}
            onToggle={handleToggleMic}
            ariaLabel={callState.localAudioEnabled ? 'Mute microphone' : 'Unmute microphone'}
            tooltip={callState.localAudioEnabled ? 'Mute' : 'Unmute'}
            isDropdownOpen={activeDropdown === 'mic'}
            onDropdownOpenChange={open => setActiveDropdown(open ? 'mic' : null)}
            popoverContainer={pipWindow?.document.body}
            dropdownContent={
              <MicDropdown
                audioInputDeviceService={audioInputDeviceService}
                isRecording={isRecording}
                session={session}
                setSelectedMicrophone={setSelectedMicrophone}
                onClose={() => setActiveDropdown(null)}
              />
            }
          />

          {/* Camera split button */}
          <SplitMediaButton
            icon={<VideocamIcon sx={{ color: 'common.white' }} />}
            inactiveIcon={<VideocamOffIcon color="error" />}
            isActive={callState.localVideoEnabled}
            onToggle={handleToggleVideo}
            ariaLabel={callState.localVideoEnabled ? 'Turn off camera' : 'Turn on camera'}
            tooltip={callState.localVideoEnabled ? 'Turn off camera' : 'Turn on camera'}
            isDropdownOpen={activeDropdown === 'camera'}
            onDropdownOpenChange={open => setActiveDropdown(open ? 'camera' : null)}
            popoverContainer={pipWindow?.document.body}
            dropdownContent={
              <CameraDropdown
                isRecording={isRecording}
                session={session}
                onClose={() => setActiveDropdown(null)}
              />
            }
          />

          {/* Screen share toggle */}
          <Tooltip
            title={
              someoneElseSharing
                ? 'Another participant is sharing'
                : isSharingScreen
                  ? 'Stop sharing'
                  : 'Share screen'
            }
          >
            <ButtonBase
              onClick={handleShareToggle}
              aria-label={isSharingScreen ? 'Stop screen share' : 'Share screen'}
              disabled={someoneElseSharing}
              sx={{
                width: 48,
                height: 48,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 1,
                '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
              }}
            >
              {isSharingScreen ? (
                <StopScreenShareIcon sx={{ fontSize: 20, color: 'error.main' }} />
              ) : (
                <ScreenShareIcon sx={{ fontSize: 20, color: 'common.white' }} />
              )}
            </ButtonBase>
          </Tooltip>

          <Tooltip title="Leave call">
            <ButtonBase
              onClick={onLeave}
              aria-label="Leave call"
              sx={{
                width: 48,
                height: 48,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'error.main',
                color: 'common.white',
                borderRadius: 1,
                '&:hover': { bgcolor: 'error.dark' },
              }}
            >
              <LogoutIcon sx={{ fontSize: 20 }} />
            </ButtonBase>
          </Tooltip>
        </Box>

        {/* Right group: PiP enter or PiP exit */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ minWidth: 0, justifySelf: 'end' }}
        >
          {isPipWindow ? (
            <Tooltip title="Close picture-in-picture">
              <ButtonBase
                onClick={onClose}
                aria-label="Close picture-in-picture"
                sx={{
                  width: 48,
                  height: 48,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 1,
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                }}
              >
                <PipExitIcon sx={{ fontSize: 20, color: 'common.white' }} />
              </ButtonBase>
            </Tooltip>
          ) : isPipSupported && onEnterPip ? (
            <Tooltip title="Picture-in-picture">
              <ButtonBase
                onClick={onEnterPip}
                aria-label="Picture-in-picture"
                sx={{
                  width: 48,
                  height: 48,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 1,
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                }}
              >
                <PipIcon sx={{ fontSize: 20, color: 'common.white' }} />
              </ButtonBase>
            </Tooltip>
          ) : null}
        </Stack>
      </Box>
    </Box>
  );
}
